import { useEffect, useState } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import "react-quill/dist/quill.snow.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CustomTypography from "../../globalStyles/fonts";
import { notificationService } from "../../utils/services/notificationService";
import { toast } from "react-toastify";
import { useForecast } from "../../context/forecastContext";
import { forecastDataService } from "../../utils/services/forecast-data.service";
import { useNavigate } from "react-router-dom";

const NotificationModal = ({
  onClose,
  data,
  activeSection,
  setActiveSection,
  notificationLoading,
  loadMoreButton,
  onClickLoadMore,
}) => {
  const [notifyLoader, setNotifyLoader] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { openForecast, setForecastLoading, setForecastData } = useForecast();
  const navigate = useNavigate();

  const handleAcceptNotification = async (event, notification) => {
    try {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      setNotifyLoader(true);
      // Call API to accept notification
      await notificationService.updateNotification(notification._id, {
        notification_status: "Read",
      });

      setNotifications((prev) =>
        prev.map((x) =>
          x._id === notification._id
            ? { ...x, notification_status: "Read" }
            : x,
        ),
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to accept notification", {
        autoClose: 3000,
      });
    } finally {
      setNotifyLoader(false);
    }
  };

  const handleRejectNotification = async (event, notification) => {
    try {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      setNotifyLoader(true);
      // Call API to reject notification
      await notificationService.updateNotification(notification._id, {
        notification_status: "Archived",
      });
      setNotifications((prev) =>
        prev.filter((x) => x._id !== notification._id),
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to reject notification", {
        autoClose: 3000,
      });
    } finally {
      setNotifyLoader(false);
    }
  };

  const onClickNotification = async (notification) => {
    handleAcceptNotification(null, notification);
    onClose();
    if (notification.type === "FORECAST_UPDATE") {
      openForecast(notification.data.sku, null);
      setForecastLoading(true);
      try {
        let res = await forecastDataService.get(
          notification.data.forecast_data_id,
        );
        setForecastData({ ...res.data, forecast_data_id: res.data._id });
      } catch (error) {
        console.error("Error fetching forecast data:", error);
        toast.error("Failed to fetch forecast data", {
          autoClose: 3000,
        });
      } finally {
        setForecastLoading(false);
      }
    } else if (notification.type === "FORECAST_CATEGORY_DONE") {
      const { forecast_data_ids = [], category } = notification.data || {};
      setForecastLoading({
        open: true,
        sku: [category],
        expectedTime: null,
      });
      try {
        const allResults = await Promise.all(
          forecast_data_ids.map((id) => forecastDataService.get(id)),
        );
        const allForecastData = allResults.map((res) => ({
          ...res.data,
          forecast_data_id: res.data._id,
        }));
        setForecastData(allForecastData, null);
      } catch (error) {
        console.error("Error fetching category forecast data:", error);
        toast.error("Failed to fetch category forecast results", {
          autoClose: 3000,
        });
      } finally {
        setForecastLoading({ open: false });
      }
    } else if (notification.type === "shipment") {
      navigate(
        `/order-flow/${encodeURIComponent(notification?.data?.order_id)}`,
      );
    } else if (
      ["Order Process", "Order Fulfillment"].includes(notification?.type)
    ) {
      notification.type === "Order Process"
        ? navigate(
            `/order-flow/${encodeURIComponent(notification?.data?.order_id)}`,
          )
        : navigate(
            `/order-tracking-history/${encodeURIComponent(notification?.data?.order)}`,
          );
    } else if (notification.type === "Inbound Delay") {
      navigate(`/asn-tracking/${encodeURIComponent(notification?.data?.po)}`);
    } else if (notification.type === "Purchase Order") {
      navigate(`/purchase-order`);
    } else if (notification.type === "Supplier Config") {
      navigate("/inventory-config", {
        state: { product: { _id: notification?.data?.product } },
      });
    } else if (notification.type === "Data Error") {
      localStorage.setItem(
        "po-filter-data",
        JSON.stringify({
          filters: [
            {
              key: "asn_no",
              values: [notification?.data?.purchase_order],
            },
          ],
        }),
      );
      navigate(`/purchase-order?child=true`);
    }
  };

  useEffect(() => {
    setNotifications(data || []);
  }, [data]);

  return (
    <div sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <ToggleButtonGroup
          value={activeSection}
          exclusive
          onChange={(event, newSection) => {
            if (newSection !== null) {
              setActiveSection(newSection);
            }
          }}
          sx={{
            backgroundColor: "var(--bg-secondary)",
            border: "1px solid var(--border-color)",
            borderRadius: "10px",
            width: "100%",
            height: 42,
            padding: "4px",
          }}
        >
          <ToggleButton
            value="General"
            sx={{
              flex: 1,
              textTransform: "capitalize",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 500,
              borderRadius: "8px",
              color: "var(--text-secondary)",
              transition: "all 0.2s ease",
              "&.Mui-selected": {
                backgroundColor: "var(--bg-paper)",
                color: "#5D5FEF",
                fontWeight: 600,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor: "var(--bg-paper)",
                },
              },
              "&:hover:not(.Mui-selected)": {
                backgroundColor: "var(--bg-primary)",
              },
            }}
          >
            General
          </ToggleButton>
          <ToggleButton
            value="Forecast"
            sx={{
              flex: 1,
              textTransform: "capitalize",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 500,
              borderRadius: "8px",
              color: "var(--text-secondary)",
              transition: "all 0.2s ease",
              "&.Mui-selected": {
                backgroundColor: "var(--bg-paper)",
                color: "#5D5FEF",
                fontWeight: 600,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor: "var(--bg-paper)",
                },
              },
              "&:hover:not(.Mui-selected)": {
                backgroundColor: "var(--bg-primary)",
              },
            }}
          >
            Forecast
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <DialogContent
        sx={{
          width: "600px",
          maxWidth: "90vw",
          position: "relative",
          backgroundColor: "var(--bg-secondary)",
        }}
      >
        {notificationLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "var(--bg-secondary)",
              zIndex: 2,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {notifications && notifications.length > 0 ? (
          <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
            {notifications.map((item, index) => (
              <Box
                key={item._id + " -- " + index}
                sx={{
                  p: 2.5,
                  mb: 2,
                  border: "1px solid var(--border-color)",
                  borderRadius: "12px",
                  backgroundColor: "var(--bg-paper)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                  mr: 2,
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    borderColor: "var(--border-color)",
                    backgroundColor: "var(--hover-bg)",
                  },
                }}
                onClick={() => onClickNotification(item)}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    fontSize="13px"
                    color="var(--text-primary)"
                    fontWeight={item.notification_status === "Read" ? 400 : 500}
                    fontFamily="Poppins"
                    sx={{ mb: 0.6, lineHeight: 1.4 }}
                  >
                    {item.message || "No message"}
                  </Typography>
                  <Typography
                    fontSize="11px"
                    color="var(--text-secondary)"
                    fontWeight={400}
                    fontFamily="Poppins"
                  >
                    {new Date(item.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    mt: 0.5,
                  }}
                >
                  <Tooltip title="Mark as Read">
                    <IconButton
                      size="small"
                      onClick={(e) => handleAcceptNotification(e, item)}
                      disabled={notifyLoader}
                      sx={{
                        color: "#10B981",
                        padding: "6px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(16, 185, 129, 0.1)",
                          color: "#059669",
                        },
                        "&:disabled": {
                          opacity: 0.5,
                        },
                      }}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Archive">
                    <IconButton
                      size="small"
                      onClick={(e) => handleRejectNotification(e, item)}
                      disabled={notifyLoader}
                      sx={{
                        color: "#EF4444",
                        padding: "6px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(239, 68, 68, 0.1)",
                          color: "#DC2626",
                        },
                        "&:disabled": {
                          opacity: 0.5,
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
            {loadMoreButton && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={onClickLoadMore}
                  disabled={notificationLoading}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    borderColor: "var(--border-color)",
                    color: "var(--text-tertiary)",
                    "&:hover": {
                      borderColor: "var(--border-color)",
                      backgroundColor: "rgba(31, 41, 55, 0.05)",
                    },
                  }}
                >
                  Load More
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <img
              src="no_notification.svg"
              alt="No notifications"
              style={{ width: 100, marginBottom: 16, opacity: 0.7 }}
            />
            <CustomTypography fontSize={14} color="var(--text-secondary)">
              No notifications yet
            </CustomTypography>
            <CustomTypography
              fontSize={12}
              color="var(--text-secondary)"
              sx={{ mt: 0.5 }}
            >
              You're all caught up!
            </CustomTypography>
          </Box>
        )}
      </DialogContent>
      {/* <DialogActions style={{ padding: 10 }}>
       <Button
          onClick={onClose}
          variant="outlined"
          style={{ textTransform: "none" }}
        >
          Cancel
        </Button>
      </DialogActions>*/}
    </div>
  );
};
export default NotificationModal;
